import { z } from "zod";
import type { AlertaDaAnamnese, ModeloDeAnamnese, Pergunta } from "./modelo";

// Respostas da anamnese: um registro por pergunta. `valor` serve a SIM_NAO, ESCOLHA, ESCALA e TEXTO;
// `valores` à MULTIPLA; `detalhe` ao "Sim" que pede mais. Ausente = não respondida (nunca "Não").

export const respostaSchema = z.object({
  valor: z.string().trim().max(4000).optional(),
  valores: z.array(z.string().max(60)).max(30).optional(),
  detalhe: z.string().trim().max(2000).optional(),
});
export type RespostaDaAnamnese = z.infer<typeof respostaSchema>;

export const respostasSchema = z
  .record(z.string().max(60), respostaSchema)
  .refine((r) => Object.keys(r).length <= 200, "Respostas demais");
export type RespostasDaAnamnese = z.infer<typeof respostasSchema>;

const respondida = (r: RespostaDaAnamnese | undefined) => !!r && (!!r.valor?.trim() || (r.valores?.length ?? 0) > 0);

/** A pergunta aparece? (depende da resposta de outra, se tiver `mostrarSe`). */
export function visivel(p: Pergunta, respostas: RespostasDaAnamnese) {
  if (!p.mostrarSe) return true;
  const r = respostas[p.mostrarSe.pergunta];
  const marcados = [r?.valor, ...(r?.valores ?? [])].filter(Boolean) as string[];
  return marcados.some((v) => p.mostrarSe!.valores.includes(v));
}

const perguntas = (m: ModeloDeAnamnese) => m.secoes.flatMap((s) => s.perguntas);

/** Só o que o modelo conhece e está visível — o resto (pergunta escondida, id estranho) sai. */
export function limparRespostas(m: ModeloDeAnamnese, respostas: RespostasDaAnamnese): RespostasDaAnamnese {
  const limpas: RespostasDaAnamnese = {};
  for (const p of perguntas(m)) {
    const r = respostas[p.id];
    if (!respondida(r) || !visivel(p, respostas)) continue;
    const opcoes = p.opcoes?.map((o) => o.valor);
    const valido = (v: string) => !opcoes || opcoes.includes(v);
    const valor = p.tipo === "MULTIPLA" ? undefined : r!.valor?.trim();
    if (valor && p.tipo !== "TEXTO" && p.tipo !== "ESCALA" && !valido(valor)) continue;
    if (valor && p.tipo === "ESCALA" && !/^(10|[0-9])$/.test(valor)) continue;
    const valores = p.tipo === "MULTIPLA" ? r!.valores?.filter(valido) : undefined;
    const detalhe = p.tipo === "SIM_NAO" && valor === "SIM" ? r!.detalhe?.trim() || undefined : undefined;
    if (p.tipo === "MULTIPLA" && !valores?.length) continue;
    limpas[p.id] = { ...(valor ? { valor } : {}), ...(valores?.length ? { valores } : {}), ...(detalhe ? { detalhe } : {}) };
  }
  return limpas;
}

export type Pendencia = { pergunta: string; secao: string; motivo: "SEM_RESPOSTA" | "SEM_DETALHE" };

/** O que falta pra poder enviar: obrigatória sem resposta e "Sim" sem o detalhe obrigatório. */
export function pendencias(m: ModeloDeAnamnese, respostas: RespostasDaAnamnese): Pendencia[] {
  const lista: Pendencia[] = [];
  for (const s of m.secoes) {
    for (const p of s.perguntas) {
      if (!visivel(p, respostas)) continue;
      const r = respostas[p.id];
      if (p.obrigatoria && !respondida(r)) lista.push({ pergunta: p.id, secao: s.id, motivo: "SEM_RESPOSTA" });
      else if (p.detalhe?.obrigatorio && r?.valor === "SIM" && !r.detalhe?.trim()) lista.push({ pergunta: p.id, secao: s.id, motivo: "SEM_DETALHE" });
    }
  }
  return lista;
}

/** Percentual das perguntas visíveis já respondidas (0–100). */
export function progresso(m: ModeloDeAnamnese, respostas: RespostasDaAnamnese) {
  const vis = perguntas(m).filter((p) => visivel(p, respostas));
  if (!vis.length) return 0;
  return Math.round((vis.filter((p) => respondida(respostas[p.id])).length / vis.length) * 100);
}

/** Códigos de alerta que as respostas disparam — só os códigos, sem detalhe clínico. */
export function alertasDasRespostas(m: ModeloDeAnamnese, respostas: RespostasDaAnamnese): AlertaDaAnamnese[] {
  const achados = new Set<AlertaDaAnamnese>();
  for (const p of perguntas(m)) {
    if (!p.alerta || !visivel(p, respostas)) continue;
    const r = respostas[p.id];
    const marcados = [r?.valor, ...(r?.valores ?? [])].filter(Boolean) as string[];
    if (marcados.some((v) => p.alerta!.quando.includes(v))) achados.add(p.alerta.codigo);
  }
  return [...achados];
}

/** Texto canônico (chaves em ordem) do que foi respondido — a base do hash da assinatura. */
export function conteudoCanonico(m: ModeloDeAnamnese, respostas: RespostasDaAnamnese) {
  const ordenado = (v: unknown): unknown =>
    Array.isArray(v) ? v.map(ordenado) : v && typeof v === "object" ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, ordenado((v as Record<string, unknown>)[k])])) : v;
  return JSON.stringify(ordenado({ modelo: m.codigo, versao: m.versao, respostas: limparRespostas(m, respostas) }));
}

/** Resposta em texto pra leitura e impressão ("Sim — Penicilina, urticária"); null = sem resposta. */
export function textoDaResposta(p: Pergunta, r: RespostaDaAnamnese | undefined): string | null {
  if (!respondida(r)) return null;
  const rotulo = (v: string) => p.opcoes?.find((o) => o.valor === v)?.rotulo ?? ({ SIM: "Sim", NAO: "Não", NAO_SEI: "Não sei" } as Record<string, string>)[v] ?? v;
  if (p.tipo === "MULTIPLA") return (r!.valores ?? []).map(rotulo).join(", ");
  if (p.tipo === "TEXTO") return r!.valor!.trim();
  if (p.tipo === "ESCALA") return `${r!.valor} de 10`;
  const base = rotulo(r!.valor!);
  return r!.detalhe ? `${base} — ${r!.detalhe}` : base;
}
